import React from 'react';
import { HtmlSerializer, type EditorPlugin } from '@aha-app/aha-editor-v2';
import Autosaver, {
  type AutosaverOptions,
  type AutosaveState,
} from './Autosaver';
import { ApplicationView, observable } from '@aha-app/mvc';
import styles from '../DocumentSavedStatePlugin/DocumentSavedState.module.css';

interface AutosaveSettings {
  url: string;
  hasSharedPath?: boolean;
}

interface AutosavePluginState {
  state: AutosaveState;
}

export interface AutosavePluginProps {
  // Autosave functionality
  autosave: AutosaveSettings;
  additionalParams?: { [key: string]: any };
  name: string;
  format?: 'html' | 'json';
  accepts?: AutosaverOptions['accepts'];
}

function AutosavePlugin(props: AutosavePluginProps): EditorPlugin {
  const { accepts, autosave, additionalParams, name, format = 'json' } = props;

  let autosaver: Autosaver;

  const autosaveState = observable<AutosavePluginState>({ state: 'idle' });

  const Indicators: React.FC = () => {
    let text = '';
    switch (autosaveState.state) {
      case 'saving':
        text = 'Saving';
        break;
      case 'saved':
        text = 'Saved';
        break;
      case 'error':
        text = 'Error saving';
        break;
      default:
        return null;
    }

    return <div className={styles.documentState}>{text}</div>;
  };

  return {
    name: 'Autosave plugin',
    handlers: {
      onInit(ahaEditor) {
        autosaver = new Autosaver({
          ...autosave,
          accepts,
          getParams: () => {
            let data: string | undefined;
            if (format === 'html') {
              const serializer = new HtmlSerializer(ahaEditor.editor);
              data = serializer.serialize();
            } else {
              const { children, metadata } = ahaEditor.editor;
              data = JSON.stringify({ children, metadata });
            }

            return Object.assign({ [name]: data }, additionalParams);
          },
          onStateChange(newState) {
            autosaveState.state = newState;
          },
        });
      },

      onBlur() {
        autosaver.runTriggeredSave();

        return false;
      },

      onChange() {
        autosaver.triggerSave();
      },
    },

    layoutComponent: {
      location: 'below',
      Component: ApplicationView(Indicators),
    },
  };
}

export default AutosavePlugin;
